import LineChart from '@c/charts/LineChart';
import { useQuery } from '@tanstack/react-query';
import { getBikeGraph } from '@util/firestore/products';
import { formatCurrency } from '@util/index';
import type { ProductDocument } from 'models/product';

const PriceGuideChart = ({ product }: { product: ProductDocument }) => {
  const hasProductDetails = !!product.year && !!product.make && !!product.model;

  const { data: bikeGraph } = useQuery({
    queryKey: ['bikeGraph', product.year, product.make, product.model],
    queryFn: () => getBikeGraph(product.year!, product.make!, product.model!),
    enabled: hasProductDetails,
  });

  if (!bikeGraph) return null;

  const bikeGraphChart = {
    labels: bikeGraph.soldDates.map((date) =>
      new Date(date).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      })
    ),
    datasets: [
      {
        label: 'sold',
        data: bikeGraph.prices,
        borderColor: '#C41719',
        pointBackgroundColor: '#C41719',
        borderWidth: 2,
        fill: true,
        backgroundColor: ['rgba(196, 23, 25, 0.2)'],
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
    ],
  };

  const options = {
    plugins: {
      legend: {
        display: false,
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        title: {
          display: true,
          text: 'Price',
        },
      },
      x: {
        title: {
          display: true,
          text: 'Date',
        },
      },
    },
  };

  return (
    <div className="flex w-full flex-col gap-12 px-8 py-[4rem] sm:px-0 md:py-[6.4rem]">
      <div className="flex flex-col">
        <div className="mb-[4rem] flex justify-between gap-3">
          <div className="flex flex-col">
            <h3 className="text-[2.5rem] font-semibold">Price History</h3>
            <p className="text-[2.1rem] font-medium text-zinc-500">
              {product.year} {product.make} {product.model}
            </p>
          </div>
        </div>
        <div className="relative h-[30rem] w-full md:aspect-[3.4] md:h-auto">
          <LineChart chartData={bikeGraphChart} options={options} />
        </div>
      </div>

      <div className="flex flex-col gap-8">
        <h3 className="text-[2.5rem] font-semibold">Historical Data</h3>

        <div className="flex w-full flex-col gap-8 sm:flex-row sm:justify-between sm:gap-8">
          <PriceGuideCard
            title="Price Range"
            badgeText="Last 12 months"
            minValue={bikeGraph.min}
            maxValue={bikeGraph.max}
          />

          <PriceGuideCard
            title="Number of Sales"
            badgeText="Last 12 months"
            value={bikeGraph.count}
            isCurrency={false}
          />

          <PriceGuideCard
            title="Avg. Sale Price"
            badgeText="Last 12 months"
            value={bikeGraph.avg}
          />
        </div>
      </div>
    </div>
  );
};

const PriceGuideCard = ({
  title,
  badgeText,
  minValue,
  maxValue,
  value,
  isCurrency = true,
}: {
  title: string;
  badgeText?: string | undefined;
  minValue?: number | undefined;
  maxValue?: number | undefined;
  value?: number | undefined;
  isCurrency?: boolean;
}) => {
  return (
    <div className="w-full rounded-[1.5rem] bg-zinc-100 px-6 py-4">
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-6">
          <p className="text-[1.8rem] font-medium text-zinc-600">{title}</p>

          {badgeText && (
            <p className="rounded-[1rem] border border-zinc-200 bg-transparent px-4 py-2 text-[1.5rem] font-medium text-zinc-500">
              {badgeText}
            </p>
          )}
        </div>

        {minValue && maxValue && (
          <div className="flex w-full items-center justify-between gap-6">
            <span className="text-[2.7rem] font-semibold text-zinc-800">
              {isCurrency ? formatCurrency(minValue) : minValue} -{' '}
              {isCurrency ? formatCurrency(maxValue) : maxValue}
            </span>
          </div>
        )}

        {!minValue && !maxValue && value && (
          <span className="text-[2.7rem] font-semibold text-zinc-800">
            {isCurrency ? formatCurrency(value) : value}
          </span>
        )}
      </div>
    </div>
  );
};

export default PriceGuideChart;
